import { Link } from 'react-router-dom';
import { buildWhatsAppLink, buildWholesaleWhatsAppLink, WHATSAPP_NUMBER, BRAND_EMAIL, BRAND_ADDRESS, SOCIAL, GOOGLE_MAPS_LINK, VISIT_HOURS } from '@/lib/api';
import { Instagram, Facebook, MapPin, Mail, Phone, Truck } from 'lucide-react';

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="border-t border-[color:var(--mb-border)] bg-[color:var(--mb-bg-alt)] mt-24">
      <div className="max-w-[1600px] mx-auto px-6 sm:px-10 py-16 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3 mb-6">
            <img src="/brand/mark.jpg" alt="Mahabir Bastralaya" className="h-14 w-auto" style={{ mixBlendMode: 'multiply' }} />
            <div className="flex flex-col leading-none">
              <span className="font-serif-display text-2xl text-[color:var(--mb-primary)] lowercase" style={{ fontWeight: 500 }}>mahabir</span>
              <span className="font-serif-display text-2xl text-[color:var(--mb-primary)] lowercase -mt-1" style={{ fontWeight: 500 }}>Bastralaya</span>
            </div>
          </div>
          <p className="font-serif-display text-2xl sm:text-3xl leading-tight max-w-md">
            A house of <em className="text-[color:var(--mb-primary)] not-italic">Nepali heritage</em> since 1999.
          </p>
          <p className="mt-6 text-[color:var(--mb-text-muted)] max-w-md leading-relaxed font-light text-sm">
            Traditional dresses across Tibetan, Tamang, Rai, Gurung, Mangar, Subba, Limbu, Newari, Pradhan, Lepcha and Bhutanese cultures. Ready-to-wear and made-to-order.
          </p>
          <div className="mt-6 flex items-center gap-3">
            <a href={SOCIAL.instagram} target="_blank" rel="noreferrer" data-testid="footer-instagram" aria-label="Instagram" className="w-10 h-10 flex items-center justify-center rounded-full border border-[color:var(--mb-border)] hover:border-[color:var(--mb-primary)] hover:text-[color:var(--mb-primary)]" style={{ transition: 'border-color 200ms ease, color 200ms ease' }}>
              <Instagram size={16} />
            </a>
            <a href={SOCIAL.facebook} target="_blank" rel="noreferrer" data-testid="footer-facebook" aria-label="Facebook" className="w-10 h-10 flex items-center justify-center rounded-full border border-[color:var(--mb-border)] hover:border-[color:var(--mb-primary)] hover:text-[color:var(--mb-primary)]" style={{ transition: 'border-color 200ms ease, color 200ms ease' }}>
              <Facebook size={16} />
            </a>
          </div>
        </div>

        <div className="md:col-span-3">
          <p className="eyebrow mb-4">Explore</p>
          <ul className="flex flex-col gap-2.5 text-sm">
            <li><Link to="/" className="hover:text-[color:var(--mb-primary)]" style={{ transition: 'color 200ms ease' }}>Home</Link></li>
            <li><Link to="/products" className="hover:text-[color:var(--mb-primary)]" style={{ transition: 'color 200ms ease' }}>Collection</Link></li>
            <li><Link to="/products?category=made-to-order" className="hover:text-[color:var(--mb-primary)]" style={{ transition: 'color 200ms ease' }}>Made to Order</Link></li>
            <li><Link to="/about" className="hover:text-[color:var(--mb-primary)]" style={{ transition: 'color 200ms ease' }}>About Us</Link></li>
            <li><Link to="/contact" className="hover:text-[color:var(--mb-primary)]" style={{ transition: 'color 200ms ease' }}>Visit / Enquire</Link></li>
            <li><a href={buildWholesaleWhatsAppLink()} target="_blank" rel="noreferrer" data-testid="footer-wholesale-link" className="hover:text-[color:var(--mb-primary)] inline-flex items-center gap-1.5" style={{ transition: 'color 200ms ease' }}><Truck size={12} /> Wholesale</a></li>
            <li><Link to="/admin/login" data-testid="footer-admin-link" className="hover:text-[color:var(--mb-primary)] text-[color:var(--mb-text-muted)]" style={{ transition: 'color 200ms ease' }}>Admin</Link></li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <p className="eyebrow mb-4">Visit / Contact</p>
          <div className="flex flex-col gap-4 text-sm">
            <a href={GOOGLE_MAPS_LINK} target="_blank" rel="noreferrer" className="flex items-start gap-3 hover:text-[color:var(--mb-primary)]" style={{ transition: 'color 200ms ease' }} data-testid="footer-address">
              <MapPin size={16} className="mt-0.5 shrink-0 text-[color:var(--mb-primary)]" />
              <span>
                Mahabir Bastralaya<br/>
                {BRAND_ADDRESS.line1}<br/>
                {BRAND_ADDRESS.line2}<br/>
                {BRAND_ADDRESS.line3}
              </span>
            </a>
            <a href={buildWhatsAppLink()} target="_blank" rel="noreferrer" className="flex items-center gap-3 hover:text-[color:var(--mb-primary)]" style={{ transition: 'color 200ms ease' }}>
              <Phone size={16} className="text-[color:var(--mb-primary)]" />
              +91 {WHATSAPP_NUMBER.slice(2, 7)} {WHATSAPP_NUMBER.slice(7)}
            </a>
            <a href={`mailto:${BRAND_EMAIL}`} className="flex items-center gap-3 hover:text-[color:var(--mb-primary)] break-all" style={{ transition: 'color 200ms ease' }} data-testid="footer-email">
              <Mail size={16} className="text-[color:var(--mb-primary)] shrink-0" />
              {BRAND_EMAIL}
            </a>
            <div className="pt-2 text-[color:var(--mb-text-muted)] text-xs">
              <p>{VISIT_HOURS.days} · {VISIT_HOURS.time}</p>
              <p>{VISIT_HOURS.closed}</p>
              <p className="mt-2 inline-flex items-center gap-1.5" data-testid="footer-ships-india"><Truck size={12} className="text-[color:var(--mb-primary)]" /> We ship across India</p>
            </div>
          </div>
        </div>
      </div>
      <div className="border-t border-[color:var(--mb-border)]">
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-[0.65rem] tracking-[0.3em] uppercase text-[color:var(--mb-text-muted)]">
          <span>© {new Date().getFullYear()} Mahabir Bastralaya · All rights reserved</span>
          <span>Est. 1999 · Jaigaon, West Bengal</span>
        </div>
      </div>
    </footer>
  );
}
